import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Card, Typography } from '@totalsoft/rocket-ui';
import Income from '@mui/icons-material/PlaylistAddCheck';
import GenericEntityFlowEditStatesTable from './GenericEntityFlowEditStatesTable';
import { Validator, Success, Failure, ValidationError } from '@totalsoft/pure-validations';
import { append, remove } from 'ramda';
import { over } from '@totalsoft/rules-algebra-react';
import { get } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { shape, required, concatFailure, items, errorMessage, fromModel, fromParent } from '@totalsoft/pure-validations';
import { GENERIC_ENTITY_FLOW_QUERY, GENERIC_ENTITY_BUSINESS_STATES_QUERY } from '../../list/queries';
import { UPDATE_GENERIC_ENTITY_FLOW_STATES } from '../../list/mutations';
import { GENERIC_ENTITY_FLOW_STATES_CHANGED } from '../../list/subscriptions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { emptyArray } from 'utils/constants';
import IconButton from 'components/common/buttons/IconButton';
import { usePermissions } from 'providers/RightsHooks';
import { FlowPermissionType, useFlowPermissions } from '../hooks';

export const uniqueStateId = list =>
  Validator(function uniqueStateId(value) {
    if (!list && list?.length === 0) return Success;

    let result = Success;
    list.forEach((listElemet, index) => {
      if (value === listElemet.stateId && index < list.length) {
        for (let i = index + 1; i < list.length; i++) {
          if (listElemet.stateId === list[i].stateId) {
            result = Failure(ValidationError());
          }
        }
      }
    });
    return result;
  });

export const uniqueInitialState = (list, stateId) =>
  Validator(function uniqueStateId(currentIsInitialStateValue) {
    if ((!list && list?.length === 0) || !currentIsInitialStateValue) return Success;

    let result = Success;
    list.forEach((listElemet, index) => {
      if (listElemet.isInitialState && stateId === listElemet.stateId) {
        list.forEach((innerListElemet, i) => {
          if (innerListElemet.isInitialState && index !== i) {
            result = Failure(ValidationError());
          }
        });
      }
    });
    return result;
  });

export const genericEntityFlowStatesValidator = fromModel(model =>
  items(
    shape({
      stateId: concatFailure(required, uniqueStateId(model) |> errorMessage('GenericEntityFlow.GenericEntityFlowUniqueState')),
      isInitialState: fromParent(
        parent => uniqueInitialState(model, parent.stateId) |> errorMessage('GenericEntityFlow.GenericEntityFlowInitialStateUnique')
      )
    })
  )
);

const GenericEntityFlowEditStatesContainer = ({
  genericEntityFlowId,
  genericEntityFlowStatesLens,
  statesDirtyInfo,
  resetStates,
  transitionsLens
}) => {
  const [statesSaving, setStatesSaving] = useState(false);
  const [validation, validate] = useDirtyFieldValidation(genericEntityFlowStatesValidator);

  const { t } = useTranslation();
  const addToast = useToast();

  const authorizationCode = useFlowPermissions(FlowPermissionType.WRITE);
  const canWrite = usePermissions(authorizationCode);

  const { refetch } = useQuery(GENERIC_ENTITY_FLOW_QUERY, {
    variables: { flowId: genericEntityFlowId },
    skip: !genericEntityFlowId,
    onCompleted: data => {
      resetStates([...data.genericEntityFlow.genericEntityFlowStates]);
    }
  });

  const { data: genericEntityBusinessStatesData } = useQuery(GENERIC_ENTITY_BUSINESS_STATES_QUERY);
  const genericEntityBusinessStates = useMemo(
    () => genericEntityBusinessStatesData?.genericEntityBusinessStates ?? emptyArray,
    [genericEntityBusinessStatesData?.genericEntityBusinessStates]
  );

  const handleAddItem = useCallback(() => {
    over(
      genericEntityFlowStatesLens,
      append({
        flowId: genericEntityFlowId,
        siteId: 0,
        stateId: undefined,
        isInitialState: false,
        activeProduct: false,
        editableYN: true,
        deletePermitted: true,
        stateColor: undefined
      })
    );
  }, [genericEntityFlowStatesLens, genericEntityFlowId]);

  const handleRemoveItem = useCallback(
    index => {
      if (window.confirm(t('GenericEntityFlow.DeleteStateQuestion'))) {
        const transitions = transitionsLens.genericEntityFlowTransitions |> get;
        const currentStateId = genericEntityFlowStatesLens[index].stateId |> get;
        if (transitions.find(x => x.initialStateId === currentStateId || x.finalStateId === currentStateId))
          addToast(t('GenericEntityFlow.DeleteStateWithTransitionValidationMessage'), 'error');
        else over(genericEntityFlowStatesLens, remove(index, 1));
      }
    },
    [genericEntityFlowStatesLens, t, transitionsLens, addToast]
  );

  useEffect(() => {
    validate(genericEntityFlowStatesLens |> get, statesDirtyInfo);
  }, [validate, genericEntityFlowStatesLens, statesDirtyInfo]);

  const [updateGenericEntityFlowState] = useMutation(UPDATE_GENERIC_ENTITY_FLOW_STATES, {
    onError: () => {
      setStatesSaving(false);
    }
  });

  useSubscription(GENERIC_ENTITY_FLOW_STATES_CHANGED, {
    onData: ({ client }) => {
      refetch();
      setStatesSaving(false);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'computeFinancialProductNextStates' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: `genericEntityFlowTransitions({"flowId":${genericEntityFlowId}})` });
      client.cache.gc();
      addToast(t('GenericEntityFlow.StatesModified'), 'success');
    }
  });

  const onSave = useCallback(() => {
    const input = genericEntityFlowStatesLens |> get;
    const flowId = genericEntityFlowId;
    if (!validate(input)) return;
    updateGenericEntityFlowState({ variables: { flowId, input } });
    setStatesSaving(true);
  }, [genericEntityFlowId, genericEntityFlowStatesLens, updateGenericEntityFlowState, validate]);

  return (
    <Card
      icon={Income}
      iconColor='primary'
      title={t('GenericEntityFlow.PossibleStates')}
      actions={
        <>
          <IconButton type='add' onClick={handleAddItem} disabled={!canWrite || statesSaving} />
          <IconButton
            type='save'
            onClick={onSave}
            disabled={!canWrite || !isDirty(statesDirtyInfo) || !(validation |> isValid) || statesSaving}
          />
        </>
      }
    >
      {
        <GenericEntityFlowEditStatesTable
          genericEntityFlowStatesLens={genericEntityFlowStatesLens}
          genericEntityBusinessStates={genericEntityBusinessStates}
          onDelete={handleRemoveItem}
          validation={validation}
        />
      }
      <Grid item xs={12}>
        <Typography color='error'>{validation |> getErrors}</Typography>
      </Grid>
    </Card>
  );
};

GenericEntityFlowEditStatesContainer.propTypes = {
  genericEntityFlowId: PropTypes.number.isRequired,
  genericEntityFlowStatesLens: PropTypes.object,
  statesDirtyInfo: PropTypes.object,
  resetStates: PropTypes.func,
  transitionsLens: PropTypes.object
};

export default GenericEntityFlowEditStatesContainer;
